import { MINNESOTA_LMI_FUNNEL_ID } from '~/funnels/minnesota-lmi/config';
import { LDClient, LDFlags } from '~/providers';
import { UTILITY_IDS } from '~/utils/constants';
import { DocuSignContext } from '../docusign';
import { SolarFunnelContext } from '../solar-funnel';

/**
 * Whether the current subscriber is part of the Illinois Adjustable Block program and requires a
 * generated disclosure form.
 */
export const usesIpaContracts = (context: SolarFunnelContext) =>
  context.funnelPacket?.utilityAccount.communitySolarProjectGroup?.usesIpaContracts || false;

/**
 * The broad docuSign use case for this context.  We only support
 * one use case at a time.  This is unrelated to the contracts in a
 * use case, of which there may be multiple bundled in a single
 * DocuSign envelope.
 */
export const docuSignUseCase = (context: SolarFunnelContext): DocuSignContext['useCase'] => {
  if (usesIpaContracts(context)) return 'ipa';
  if (isNewMexico(context)) return 'nm';
  if (isMassachusetts(context)) return 'ma';
  /**
   * the mn lmi check must be done first. before isXcelEnergyFellbackToAccountNumber
   * because isXcelEnergyFellbackToAccountNumber is a superSet of isMinnesotaLMI.
   * (i.e) isXcelEnergyFellbackToAccountNumber is true for ALL minnesota utility.
   * But isMinnesotaLMI is only true for Minnesota Utilities IF the user uses the /mn-lmi funnel.
   * so if isMinnesotaLMI dont come before isXcelEnergyFellbackToAccountNumber,
   * then isXcelEnergyFellbackToAccountNumber will highjack the condition chain.
   */
  if (isMinnesotaLMI(context)) return 'mn_lmi';
  if (isIllinois(context)) return 'il';
  if (isXcelEnergyFellbackToAccountNumber(context)) return 'usage_request';
  return 'lmi';
};

const isXcelEnergyFellbackToAccountNumber = context => {
  /**
   * The funnelId check is very important because isXcelEnergyFellbackToAccountNumber
   * is for legacy programs (commsolar) and the isMinnesotaLMI is exclusively for the new LMI programs.
   * Both are Xcel Energy programs, but the legacy programs are not following the mn-lmi funnel.
   */
  const isXcelEnergy =
    [UTILITY_IDS['Xcel Energy CO'], UTILITY_IDS['Xcel Energy MN']].includes(
      context.utilitySelection?.id,
    ) && context.funnelId !== MINNESOTA_LMI_FUNNEL_ID;

  return (
    isXcelEnergy &&
    LDClient.variation(LDFlags.accountNumberBackup) &&
    ['error', 'timed_out'].includes(context.utilityConnectStatus!)
  );
};

const isMinnesotaLMI = (context: SolarFunnelContext) => {
  return (
    context.funnelPacket?.utility.state.abbreviation === 'MN' &&
    context.funnelId === MINNESOTA_LMI_FUNNEL_ID
  );
};

const isIllinois = (context: SolarFunnelContext) => {
  return context.funnelPacket?.utility.state.abbreviation === 'IL';
};

/**
 * Logic to determine whether a subscriber should enter the docuSign
 * machine. Primarily determined by the docuSignContracts returned
 * by the fetchVnmTerms service.
 */
export const needsDocuSign = (context: SolarFunnelContext) => {
  const nonLmiDocuSign = context.docuSignContracts?.filter(c => c.contractType !== 'lmi');
  const state = context.funnelPacket?.utility.state.abbreviation;
  switch (state) {
    case 'NM':
      return LDClient.variation(LDFlags.newMexicoDocusign);
    case 'MN':
    case 'CO':
      return isXcelEnergyFellbackToAccountNumber(context);
    default:
      return !!nonLmiDocuSign?.length || isLmiandUseEnvelopes(context);
  }
};
const isNewMexico = (context: SolarFunnelContext) =>
  context.funnelPacket?.utility.state.abbreviation === 'NM';
const isMassachusetts = (context: SolarFunnelContext) =>
  context.funnelPacket?.utility.state.abbreviation === 'MA';

const isLmiandUseEnvelopes = (context: SolarFunnelContext): boolean =>
  isLmi(context) && LDClient.variation(LDFlags.useEnvelopeForLmiForm);

const isLmi = (context: SolarFunnelContext): boolean =>
  context.lmiStatus === 'isLmi' || context.lmiStatus === 'maybeLmi';
